import { AfterViewInit, Component, effect, inject, input, OnDestroy, OnInit, output, signal, TemplateRef, viewChild } from '@angular/core';
import { ValidationErrors, ValidatorFn } from '@angular/forms';
import { LocationService } from '../../../../service/location.service';
import { UserLoginService } from '../../../../service/user-login.service';
import {
  AssignmentDetails,
  IncidentFormContext,
  IncidentFormError,
  IncidentFormGroup,
  IncidentFormValues,
  LiveCamera,
  LocationResponse,
  ReviewStatus,
} from '../../../../types';
import { atLeastOneExtraLinkValidator, defaultIncidentFormValues, incidentFormValidator } from '../review-submit-form/form-utils';
import { ReviewFormService } from '../review-submit-form/review-form.service';
import { AssignmentDetailsStore } from '../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-sideloaded-incident-form',
  templateUrl: './sideloaded-incident-form.component.html',
  styleUrl: './sideloaded-incident-form.component.scss',
  standalone: false,
})
export class SideloadedIncidentFormComponent implements OnInit, AfterViewInit, OnDestroy {
  public reviewFormService = inject(ReviewFormService);
  private locationService = inject(LocationService);
  private userLoginService = inject(UserLoginService);
  public assignmentDetailsStore = inject(AssignmentDetailsStore);
  public isSubmitting = signal<boolean>(false);
  public isLoading = signal<boolean>(false);
  public locationId = signal<number | undefined>(undefined);
  public location = signal<LocationResponse | undefined>(undefined);
  public liveCameras = signal<LiveCamera[] | undefined>(undefined);
  public instructions = signal<string | undefined>(undefined);
  public notes = signal<string | undefined>(undefined);
  public reviewFormInitialValues = signal<IncidentFormValues | undefined>(undefined);
  public isAssignedToOther = signal<boolean | undefined>(undefined);
  public locationTimezone = input<string | undefined>();
  public formRef = viewChild<TemplateRef<unknown>>('formBody');
  public contextReady = output<IncidentFormContext>();
  public contextError = output<unknown>();
  public formRefReady = output<TemplateRef<unknown>>();

  constructor() {
    effect(() => {
      const details = this.assignmentDetailsStore.details();
      if (!details) return;
      this.loadSideloadedAssignment(details);
    });
  }

  public ngOnInit() {
    this.reviewFormService.form.setValidators(this.sideloadedAssignmentValidator);
    this.reviewFormService.form.updateValueAndValidity({ emitEvent: false });
  }

  public ngAfterViewInit(): void {
    this.formRefReady.emit(this.formRef());
  }

  public ngOnDestroy(): void {
    this.reviewFormService.form.setValidators(incidentFormValidator);
  }

  private loadSideloadedAssignment(details: AssignmentDetails) {
    if (!details) {
      this.contextError.emit(new Error('Assignment details not available'));
      return;
    }

    this.locationId.set(details.location_id);
    // these are location-level instructions that the LP teams can include
    // (e.g. "all cameras in this location have a 16-minute delay in the livestream")
    this.instructions.set(details.instructions);
    // external links to the market cameras
    this.liveCameras.set(details.live_streams);
    // when sideloaded assignments are created, the creator can optionally share notes (like additional context about the location)
    this.notes.set(details.notes ?? '');

    const isAssignedToOther =
      details.status === ReviewStatus.Assigned &&
      details.user_id !== null &&
      details.user_id !== this.userLoginService.currentProfile()?.user_id &&
      !this.userLoginService.currentUserIsAdmin();
    this.reviewFormService.form.setErrors(isAssignedToOther ? { [IncidentFormError.ASSIGNED_TO_OTHER]: true } : null);

    this.reviewFormInitialValues.set({ ...defaultIncidentFormValues });
    this.reviewFormService.caseImages.set(details.case_images);

    this.contextReady.emit({
      locationId: details.location_id,
      instanceAtDate: undefined,
      form: this.reviewFormService.form,
    });

    this.locationService.getLocation({ locationId: details.location_id }).subscribe((location) => this.location.set(location));
  }

  private readonly sideloadedAssignmentValidator: ValidatorFn = (form: IncidentFormGroup): ValidationErrors | null => {
    if (!form.controls.isTheft.value) return null;
    const baseErrors = incidentFormValidator(form) || {};
    const needsExtraLink = atLeastOneExtraLinkValidator(form.controls.extraLinks);
    const extraLinkErrors = needsExtraLink ? { [IncidentFormError.EXTRA_LINKS_REQUIRED]: true } : {};
    const merged = { ...baseErrors, ...extraLinkErrors } as ValidationErrors;
    return Object.keys(merged).length > 0 ? merged : null;
  };
}
